import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TeamAvatar} from '../../../../components/common/avatars/teamAvatar';
import {TeamDropdownItemContainer} from '../../../../components/common/dropdowns/teamDropdownItemContainer';
import {UserDropdownItem} from '../../../../components/common/dropdowns/userDropdownItem';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {TeamModel} from '../../../../models/api/teamModel';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../../store/data/teams/teamsSelectors';

/*
 * Props.
 */

interface BadgeNamespacePreferenceControl {
  namespace: string | undefined;
  onNamespaceChange: HandlerOf<string | undefined>;
}

/*
 * Styles.
 */

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledLabelDiv = styled.div`
  ${ellipsis()};
`;

/*
 * Component.
 */

// TODO: De-dupe with NamespaceDropdown.
export const BadgeNamespacePreferenceControl: FC<BadgeNamespacePreferenceControl> = (props) => {
  const userNamespace = useAppSelector(getUserNamespace);
  const teams = useAppSelector(getUserTeams);

  return (
    <DropdownBoxCoordinator
      value={renderValue(userNamespace, teams, props.namespace)}
      renderDropdown={(popoverProps) => renderDropdown(userNamespace, teams, popoverProps, props)}
      noInputMarginRight
    />
  );
};

const renderNoneLabel = () => <Trans id="teammatepreferences-badgenamespace-none">Disable counters</Trans>;
const renderUserLabel = () => <Trans id="teammatepreferences-badgenamespace-user">My inbox</Trans>;

function renderValue(
  userNamespace: string,
  teams: readonly TeamModel[],
  selectedNamespace: string | undefined,
): ReactNode {
  if (selectedNamespace === userNamespace) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="lockSmall" color={theme.greys.shade60} />
            </StyledIconDiv>
            <StyledLabelDiv>{renderUserLabel()}</StyledLabelDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  const selectedTeam = teams.find((team) => team.namespace === selectedNamespace);
  if (selectedTeam) {
    return (
      <StyledValueDiv>
        <StyledIconDiv>
          <TeamAvatar team={selectedTeam} size={VisualSizesEnum.SMALL} />
        </StyledIconDiv>
        <StyledLabelDiv>{selectedTeam.name}</StyledLabelDiv>
      </StyledValueDiv>
    );
  }

  return (
    <StyledValueDiv>
      <StyledLabelDiv>{renderNoneLabel()}</StyledLabelDiv>
    </StyledValueDiv>
  );
}

function renderDropdown(
  userNamespace: string,
  teams: readonly TeamModel[],
  popoverProps: PopoverTransferProps,
  props: BadgeNamespacePreferenceControl,
): ReactNode {
  const {namespace: selectedNamespace, onNamespaceChange} = props;
  const isUserSelected = userNamespace === selectedNamespace;
  const selectedTeam = teams.find((team) => team.namespace === selectedNamespace);
  const isNoneSelected = !isUserSelected && isUndefined(selectedTeam);

  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        <SimpleDropdownItem isChecked={isNoneSelected} onSelect={() => onNamespaceChange(undefined)}>
          {renderNoneLabel()}
        </SimpleDropdownItem>
        <UserDropdownItem
          isChecked={isUserSelected}
          onSelect={() => onNamespaceChange(userNamespace)}
          renderUserItemName={renderUserLabel}
        />
        {teams.map((team) => (
          <TeamDropdownItemContainer
            key={team.id}
            team={team}
            isChecked={team === selectedTeam}
            onSelect={() => onNamespaceChange(team.namespace)}
          />
        ))}
      </DropdownList>
    </Dropdown>
  );
}
